import type { AvailableSlot, Provider, Service } from '@/types/booking';

export interface NormalizedSlot {
  id: string | number;
  startTime: string;
  endTime: string;
  available: boolean;
  booked: boolean;
  providerId: number | null;
  providerName: string;
  serviceId: number | null;
  serviceName: string;
  maxCapacity: number;
  currentBookings: number;
  spotsRemaining: number;
  isFull: boolean;
  waitlistEnabled: boolean;
  capacityPercentage: number;
  organizationId: number | null;
  createdAt: string;
  updatedAt: string;
  // Preserve embedded objects for downstream use
  provider: AvailableSlot['provider'] | null;
  service: AvailableSlot['service'] | null;
  booking: AvailableSlot['booking'] | null;
  // Flat booking info
  bookingId: number | null;
  bookingPublicId: string;
  bookingStatus: string;
  // Extra fields from availability API
  durationMinutes: number | null;
}

export function normalizeSlot(slot: any): NormalizedSlot {
  const maxCapacity = slot.maxCapacity ?? slot.max_capacity ?? 1;
  const currentBookings = slot.currentBookings ?? slot.current_bookings ?? 0;
  const spotsRemaining = slot.spotsRemaining ?? slot.spots_remaining ?? maxCapacity;
  const providerObj = slot.provider ?? null;
  const providerName =
    providerObj?.displayName ??
    providerObj?.name ??
    '';

  return {
    id: slot.id ?? '',
    startTime: slot.startTime ?? slot.start_time ?? '',
    endTime: slot.endTime ?? slot.end_time ?? '',
    available: slot.available ?? true,
    booked: slot.booked ?? false,
    providerId: slot.providerId ?? slot.provider_id ?? providerObj?.id ?? null,
    providerName,
    serviceId: slot.serviceId ?? slot.service_id ?? slot.service?.id ?? null,
    serviceName: slot.service?.name ?? '',
    maxCapacity,
    currentBookings,
    spotsRemaining,
    isFull: slot.isFull ?? slot.is_full ?? spotsRemaining === 0,
    waitlistEnabled: slot.waitlistEnabled ?? slot.waitlist_enabled ?? slot.service?.waitlist_enabled ?? false,
    capacityPercentage: slot.capacityPercentage ?? slot.capacity_percentage ?? 0,
    organizationId: slot.organizationId ?? slot.organization_id ?? null,
    createdAt: slot.createdAt ?? slot.created_at ?? '',
    updatedAt: slot.updatedAt ?? slot.updated_at ?? '',
    provider: providerObj,
    service: slot.service ?? null,
    booking: slot.booking ?? null,
    bookingId: slot.booking_id ?? null,
    bookingPublicId: slot.booking_public_id ?? '',
    bookingStatus: slot.booking_status ?? '',
    durationMinutes: slot.duration_minutes ?? null,
  };
}
